import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { 
  contaService, 
  ContaType, 
  CriarContaDto, 
  AlterarContaDto,
  TransferirDto,
  SacarDto,
  DepositarDto
} from "services/contaService";

// Query keys
export const contaKeys = {
  all: ['conta'] as const,
  list: () => [...contaKeys.all, 'list'] as const,
  detail: (id: string) => [...contaKeys.all, 'detail', id] as const,
  extrato: (id: string, pageSize?: number, pageIndex?: number) => 
    [...contaKeys.all, 'extrato', id, { pageSize, pageIndex }] as const,
};

// Query para obter todas as contas
export const useContas = () => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: contaKeys.list(),
    queryFn: () => contaService.obterContas(),
    retry: getQueryRetryHandler({
      queryKey: contaKeys.list(),
      defaultMessage: "Você não possui permissão para consultar contas."
    }),
  });
};

// Query para obter uma conta específica
export const useConta = (id: string, enabled: boolean = true) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: contaKeys.detail(id),
    queryFn: () => contaService.obterConta(id),
    enabled: !!id && enabled,
    retry: getQueryRetryHandler({
      queryKey: contaKeys.detail(id),
      defaultMessage: "Você não possui permissão para consultar esta conta."
    }),
  });
};

// Query para obter extrato de uma conta
export const useExtrato = (contaId: string, pageSize: number = 20, pageIndex: number = 0, enabled: boolean = true) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: contaKeys.extrato(contaId, pageSize, pageIndex),
    queryFn: () => contaService.obterExtrato(contaId, pageSize, pageIndex),
    enabled: !!contaId && enabled,
    placeholderData: keepPreviousData,
    retry: getQueryRetryHandler({
      queryKey: contaKeys.extrato(contaId, pageSize, pageIndex),
      defaultMessage: "Você não possui permissão para consultar o extrato desta conta."
    }),
  });
};

// Mutation para criar conta
export const useCriarConta = () => {
  const queryClient = useQueryClient();
  const { getQueryRetryHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (dto: CriarContaDto) => contaService.criarConta(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contaKeys.all });
    },
    retry: getQueryRetryHandler({
      queryKey: contaKeys.all,
      defaultMessage: "Você não possui permissão para criar contas."
    }),
  });
};

// Mutation para alterar conta
export const useAlterarConta = () => {
  const queryClient = useQueryClient();
  const { getQueryRetryHandler } = usePermissionModal();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: AlterarContaDto }) => 
      contaService.alterarConta(id, dto),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: contaKeys.all });
      queryClient.invalidateQueries({ queryKey: contaKeys.detail(variables.id) });
    },
    retry: getQueryRetryHandler({
      queryKey: contaKeys.all,
      defaultMessage: "Você não possui permissão para alterar contas."
    }),
  });
};

// Mutation para excluir conta
export const useExcluirConta = () => {
  const queryClient = useQueryClient();
  const { getQueryRetryHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (id: string) => contaService.excluirConta(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: contaKeys.all });
      queryClient.removeQueries({ queryKey: contaKeys.detail(id) });
    },
    retry: getQueryRetryHandler({
      queryKey: contaKeys.all,
      defaultMessage: "Você não possui permissão para excluir contas."
    }),
  });
};

// Mutation para transferir
export const useTransferir = () => {
  const queryClient = useQueryClient();
  const { getQueryRetryHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (dto: TransferirDto) => contaService.transferir(dto),
    onSuccess: (_, variables) => {
      // Invalidar lista de contas
      queryClient.invalidateQueries({ queryKey: contaKeys.list() });
      
      // Invalidar contas específicas
      queryClient.invalidateQueries({ queryKey: contaKeys.detail(variables.contaOrigemId) });
      queryClient.invalidateQueries({ queryKey: contaKeys.detail(variables.contaDestinoId) });
      
      // Invalidar extratos
      queryClient.invalidateQueries({ queryKey: ['conta', 'extrato'] });
    },
    retry: getQueryRetryHandler({
      queryKey: contaKeys.all,
      defaultMessage: "Você não possui permissão para transferir contas."
    }),
  });
};

// Mutation para sacar
export const useSacar = () => {
  const queryClient = useQueryClient();
  const { getQueryRetryHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (dto: SacarDto) => contaService.sacar(dto),
    onSuccess: (_, variables) => {
      // Invalidar lista de contas
      queryClient.invalidateQueries({ queryKey: contaKeys.list() });
      
      // Invalidar conta específica
      queryClient.invalidateQueries({ queryKey: contaKeys.detail(variables.contaId) });
      
      // Invalidar extratos
      queryClient.invalidateQueries({ queryKey: ['conta', 'extrato'] });
    },
    retry: getQueryRetryHandler({
      queryKey: contaKeys.all,
      defaultMessage: "Você não possui permissão para sacar contas."
    }),
  });
};

// Mutation para depositar
export const useDepositar = () => {
  const queryClient = useQueryClient();
  const { getQueryRetryHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (dto: DepositarDto) => contaService.depositar(dto),
    onSuccess: (_, variables) => {
      // Invalidar lista de contas
      queryClient.invalidateQueries({ queryKey: contaKeys.list() });
      
      // Invalidar conta específica
      queryClient.invalidateQueries({ queryKey: contaKeys.detail(variables.contaId) });
      
      // Invalidar extratos
      queryClient.invalidateQueries({ queryKey: ['conta', 'extrato'] });
    },
    retry: getQueryRetryHandler({
      queryKey: contaKeys.all,
      defaultMessage: "Você não possui permissão para depositar contas."
    }),
  });
}; 